<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { formatNumber } from "@core/helpers"
import { Denivele } from "@types"
import { computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            saison_haute: {
                title: "Saison Haute",
                tooltip:
                    "La saison haute correspond à la période du 1er novembre au 31 mars inclus.",
            },
            saison_basse: {
                title: "Saison Basse",
                tooltip:
                    "La saison basse correspond à la période du 1er avril au 31 octobre inclus.",
            },
            pointe: {
                title: "Pointe fixe",
                tooltip:
                    "Les Heures de Pointe représentent jusqu'à 15 jours par an, répartis entre décembre et février, soit entre 8h et 12h, soit entre 17h et 21h. Elles sont déterminées par RTE la veille pour le lendemain.",
            },
            hpe: {
                label: "HP",
                tooltip: "Heures Pleines Basse",
            },
            hce: {
                label: "HC",
                tooltip: "Heures Creuses Basse",
            },
            hph: {
                label: "HP",
                tooltip: "Heures Pleines Haute",
            },
            hch: {
                label: "HC",
                tooltip: "Heures Creuses Haute",
            },
            hp: {
                label: "HP",
                tooltip: "Heures Pleines",
            },
            hc: {
                label: "HC",
                tooltip: "Heures Creuses",
            },
            base: {
                label: "Base",
                tooltip: "Tarif Base",
            },
            value_plage: "<strong>{plage} :</strong> {value} {unit}",
            value_without_plage: "<strong>{value} {unit}</strong>",
        },
    },
})
type Props = {
    /** Données de dénivelé de puissance */
    denivele?: Denivele
    unit?: string
}

const props = defineProps<Props>()

/**
 * Trouve une valeur de dénivelé par type de période
 * @param type - Type de période (HPH, HPB, HCH, HCB)
 * @returns Valeur du dénivelé ou 0 si non trouvé
 */
function getDeniveleValueByType(type: string): number | null {
    if (!props.denivele) {
        return null
    }
    return props.denivele[type as keyof Denivele] ?? null
}

const getUnitByDenivele = computed(() => {
    return props.denivele?.PTE != null ? "kVA" : "kW"
})

type DenivelePlage = {
    title: string
    tootlip?: string
    values: {
        label?: string
        tooltip?: string
        value: number | null
        unit: string
    }[]
}
const getDenivele = computed<DenivelePlage[]>(() => {
    if (Object.keys(props.denivele ?? {}).find((v) => v === "Base") != null) {
        return [
            {
                title: "",
                values: [
                    {
                        label: "",
                        value: getDeniveleValueByType("Base"),
                        unit: props.unit || getUnitByDenivele.value,
                    },
                ],
            },
        ]
    }

    if (
        Object.keys(props.denivele ?? {}).find((v) =>
            ["HP", "HC"].includes(v)
        ) != null
    ) {
        return [
            {
                title: "",
                values: [
                    {
                        label: t("hp.label"),
                        tooltip: t("hp.tooltip"),
                        value: getDeniveleValueByType("HP"),
                        unit: props.unit || getUnitByDenivele.value,
                    },
                    {
                        label: t("hc.label"),
                        tooltip: t("hc.tooltip"),
                        value: getDeniveleValueByType("HC"),
                        unit: props.unit || getUnitByDenivele.value,
                    },
                ],
            },
        ]
    }

    const plages: DenivelePlage[] = [
        {
            title: t("saison_haute.title"),
            tootlip: t("saison_haute.tooltip"),
            values: [
                {
                    label: t("hph.label"),
                    tooltip: t("hph.tooltip"),
                    value: getDeniveleValueByType("HPH"),
                    unit: props.unit || getUnitByDenivele.value,
                },
                {
                    label: t("hch.label"),
                    tooltip: t("hch.tooltip"),
                    value: getDeniveleValueByType("HCH"),
                    unit: props.unit || getUnitByDenivele.value,
                },
            ],
        },
        {
            title: t("saison_basse.title"),
            tootlip: t("saison_basse.tooltip"),
            values: [
                {
                    label: t("hpe.label"),
                    tooltip: t("hpe.tooltip"),
                    value: getDeniveleValueByType("HPE"),
                    unit: props.unit || getUnitByDenivele.value,
                },
                {
                    label: t("hce.label"),
                    tooltip: t("hce.tooltip"),
                    value: getDeniveleValueByType("HCE"),
                    unit: props.unit || getUnitByDenivele.value,
                },
            ],
        },
    ]
    if (getDeniveleValueByType("PTE") !== null) {
        plages.push({
            title: t("pointe.title"),
            tootlip: t("pointe.tooltip"),
            values: [
                {
                    label: "",
                    value: getDeniveleValueByType("PTE"),
                    unit: props.unit || getUnitByDenivele.value,
                },
            ],
        })
    }
    return plages
})
</script>

<template>
    <div class="flex flex-col">
        <div
            v-for="(plage, index) in getDenivele"
            :key="index"
            class="text-sm text-cyan-400 flex gap-2 md:flex-row flex-wrap flex-col md:items-center"
        >
            <p
                v-if="plage.title"
                class="font-bold w-[6.5rem] md:w-[7rem] flex items-center gap-2 justify-between text-xs md:text-sm"
            >
                {{ plage.title }}
                <i
                    class="pi pi-info-circle cursor-help"
                    v-tooltip="plage.tootlip"
                    style="font-size: 0.8rem"
                ></i>
            </p>
            <p class="md:visible hidden">•</p>
            <div
                v-for="(value, i) in plage.values"
                :key="i"
                class="flex items-center gap-2"
            >
                <div class="text-gray-500 flex items-center gap-2 flex-wrap">
                    <p
                        v-if="value.label"
                        v-tooltip="value.tooltip"
                        class="font-bold text-xs md:text-sm"
                        :class="value.tooltip ? 'cursor-help' : ''"
                    >
                        {{ value.label }}
                    </p>
                    <p>
                        {{
                            formatNumber(value.value, {
                                maximumFractionDigits: 3,
                                minimumFractionDigits: 0,
                                unit: value.unit,
                            })
                        }}
                    </p>
                </div>
                <p
                    v-if="i < plage.values.length - 1"
                    class="md:visible invisible"
                >
                    •
                </p>
            </div>
        </div>
    </div>
</template>
